/**
 * Yotpo Loyalty custom actions.
 *
 * Records a Yotpo "Custom Action" earning rule for the signed-in customer via
 * the /apps/yotpo-loyalty app proxy (nec-yotpo-loyalty Azure Function), which
 * holds the Yotpo credentials. The proxy identifies the customer from the
 * Shopify-signed `logged_in_customer_id` query param; the email/customer_id in
 * the body are hints only.
 *
 * Request:  POST /apps/yotpo-loyalty { action, email, customer_id }
 * Response: { ok, awarded } -- ok=true means Yotpo accepted the action (awarded
 *           now, or the rule's per-customer cap was already reached).
 *
 * Fire-and-forget. The session guard is set only on ok=true, so failures may
 * retry on a later save; Yotpo's rule cap is the authoritative once-per-customer
 * guard.
 */

(function () {
  var ENDPOINT = '/apps/yotpo-loyalty';
  var ACTIONS = {
    CHOOSE_TEAMS: 'new_era_choose_teams',
    SILHOUETTE_FINDER_COMPLETED: 'new_era_silhouette_finder_completed',
  };
  var pending = {};

  function signedInCustomer() {
    var customer = window.customer;
    if (!customer || !customer.id || !customer.email) return null;
    if (window.Shopify && window.Shopify.designMode) return null;
    return customer;
  }

  function sameEmail(a, b) {
    return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
  }

  function guardKey(action, customerId) {
    return 'ne.loyalty.' + action + '.' + customerId;
  }

  function isGuarded(key) {
    try { return sessionStorage.getItem(key) === '1'; } catch (error) { return false; }
  }

  function setGuard(key) {
    try { sessionStorage.setItem(key, '1'); } catch (error) { /* storage unavailable */ }
  }

  function record(action) {
    var customer = signedInCustomer();
    if (!customer) return Promise.resolve(null);

    var key = guardKey(action, customer.id);
    if (isGuarded(key)) return Promise.resolve(null);
    if (pending[key]) return pending[key];

    pending[key] = fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        action: action,
        email: customer.email,
        customer_id: String(customer.id),
      }),
    })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (result) {
        if (!result || !result.ok) return null;
        setGuard(key);
        window.dispatchEvent(new CustomEvent('ne:loyalty-action', {
          detail: { action: action, awarded: Boolean(result.awarded) },
        }));
        return result;
      })
      .catch(function () { return null; })
      .finally(function () { delete pending[key]; });

    return pending[key];
  }

  /**
   * Choose Teams goal: any save (first or update) with at least one team, made
   * for the signed-in customer's own email.
   */
  function recordChooseTeams(teams, email) {
    var customer = signedInCustomer();
    if (!customer || !teams || teams.length === 0) return Promise.resolve(null);
    if (email && !sameEmail(email, customer.email)) return Promise.resolve(null);
    return record(ACTIONS.CHOOSE_TEAMS);
  }

  function recordSilhouetteFinderCompleted() {
    return record(ACTIONS.SILHOUETTE_FINDER_COMPLETED);
  }

  window.neLoyaltyActions = {
    ACTIONS: ACTIONS,
    record: record,
    recordChooseTeams: recordChooseTeams,
    recordSilhouetteFinderCompleted: recordSilhouetteFinderCompleted,
  };
})();
